import { SECTION_META, type SectionNode } from "@baze/schema";
import type { CSSProperties, ReactNode } from "react";
import { domId, field, messageVars, titleId, type RenderContext } from "./context";
import {
  formatRupiah,
  imageSrc,
  isExternalHref,
  objectPosition,
  paragraphs,
  safeHref,
  srcSet,
  telHref,
  waLink,
  type ImageLike,
} from "./helpers";
import { Icon, type IconName } from "./icons";
import { emphasisParts, sectionStyleAttrs, textStyleCss } from "./section-style";

export const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");

// ── Layout ─────────────────────────────────────────────────────────────────────

/** Space between a section header and its content, scaled by the spacing token. */
export const STACK = "mt-(--site-gap) @3xl:mt-(--site-gap-lg)";

/** Scroll-reveal hooks (styles.css → Motion). Inert unless the root has data-motion="on". */
export const REVEAL = "baze-reveal";
/** Reveal every direct child in turn (grids and lists). */
export const STAGGER = "baze-stagger";

/** Lift + deeper shadow on hover, for cards. */
export const CARD_HOVER =
  "transition-[translate,box-shadow,border-color] duration-300 ease-out hover:-translate-y-1 hover:shadow-site-md motion-reduce:transition-none motion-reduce:hover:translate-y-0";

/** `--baze-d` stagger index for `.baze-enter` elements. */
export const enterDelay = (i: number): CSSProperties => ({ ["--baze-d" as string]: i }) as CSSProperties;

const CONTAINER = {
  narrow: "max-w-3xl",
  default: "max-w-6xl",
  wide: "max-w-7xl",
} as const;

export function Container({ children, className, size = "default" }: { children: ReactNode; className?: string; size?: keyof typeof CONTAINER }) {
  return <div className={cx("mx-auto w-full px-5 @3xl:px-8", CONTAINER[size], className)}>{children}</div>;
}

type ShellTag = "section" | "header" | "footer" | "div";

/**
 * Root element of every section: anchor id, tone, landmarks, and editor attributes
 * (data-baze-section / data-baze-type / data-selected / data-hovered / data-baze-hidden).
 */
export function SectionShell({
  ctx,
  node,
  as: Tag = "section",
  className,
  style,
  children,
  labelledBy = true,
  padded = true,
}: {
  ctx: RenderContext;
  node: SectionNode;
  as?: ShellTag;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
  /** true: aria-labelledby the section title; false: aria-label from the section type label. */
  labelledBy?: boolean;
  padded?: boolean;
}) {
  const editor = ctx.editor;
  const owner = sectionStyleAttrs(node.style, ctx.theme, node.type);
  const textCss = textStyleCss(domId(ctx, node.anchor), node.style?.texts, ctx.theme);
  const label = Tag === "section" ? (labelledBy ? { "aria-labelledby": titleId(ctx, node) } : { "aria-label": SECTION_META[node.type]?.label ?? node.type }) : {};
  return (
    <Tag
      id={domId(ctx, node.anchor)}
      data-tone={node.tone ?? "default"}
      {...owner.data}
      className={cx("baze-section bg-site-bg text-site-text", padded && "baze-pad py-(--site-section-y) @3xl:py-(--site-section-y-lg)", className)}
      style={Object.keys(owner.vars).length ? { ...owner.vars, ...style } : style}
      {...label}
      {...(editor
        ? {
            "data-baze-section": node.id,
            "data-baze-type": node.type,
            ...(ctx.selectedSectionId === node.id ? { "data-selected": "" } : {}),
            ...(ctx.hoveredSectionId === node.id ? { "data-hovered": "" } : {}),
            ...(node.hidden ? { "data-baze-hidden": "" } : {}),
          }
        : {})}
    >
      {editor && node.hidden ? <HiddenBadge /> : null}
      {/* Validated selectors and values only (see textStyleCss). */}
      {textCss ? <style dangerouslySetInnerHTML={{ __html: textCss }} /> : null}
      {children}
    </Tag>
  );
}

export function HiddenBadge() {
  return (
    <span className="baze-hidden-badge absolute top-3 left-3 z-30 inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white shadow">
      Disembunyikan
    </span>
  );
}

// ── Typography ─────────────────────────────────────────────────────────────────

export function Eyebrow({ ctx, text, path, className }: { ctx: RenderContext; text?: string; path?: string; className?: string }) {
  if (!text) return null;
  return (
    <p className={cx("baze-eyebrow text-xs font-semibold tracking-[0.18em] text-site-primary uppercase @3xl:text-[0.8125rem]", className)} {...(path ? field(ctx, path) : {})}>
      {text}
    </p>
  );
}

/** Title tag for a section: the page's single h1, otherwise h2. */
export function headingTag(ctx: RenderContext, node: { id: string }): "h1" | "h2" {
  return ctx.h1SectionId === node.id ? "h1" : "h2";
}

/** h1 or h2 with the same props (the page's single h1 is chosen by the renderer). */
export function Heading({ level, children, ...rest }: { level: "h1" | "h2"; children: ReactNode; id?: string; className?: string; style?: CSSProperties; "data-field"?: string }) {
  return level === "h1" ? <h1 {...rest}>{children}</h1> : <h2 {...rest}>{children}</h2>;
}

/** A section title's text, with its emphasised phrase (owner styling) wrapped for the effect. */
export function TitleText({ ctx, node, text }: { ctx: RenderContext; node: SectionNode; text: string }) {
  const em = emphasisParts(text, node.style, ctx.theme.tokens.colors.accent);
  if (!em) return <>{text}</>;
  return (
    <>
      {em.parts[0]}
      <span className="baze-em" data-em={em.effect} style={em.vars as CSSProperties}>
        {em.parts[1]}
      </span>
      {em.parts[2]}
    </>
  );
}

export function SectionTitle({ ctx, node, text, path = "content.title", className }: { ctx: RenderContext; node: SectionNode; text: string; path?: string; className?: string }) {
  return (
    <Heading
      level={headingTag(ctx, node)}
      id={titleId(ctx, node)}
      className={cx("baze-heading baze-title text-[2rem] leading-[1.1] @3xl:text-[2.75rem] @5xl:text-5xl", className)}
      {...field(ctx, path)}
    >
      {path === "content.title" ? <TitleText ctx={ctx} node={node} text={text} /> : text}
    </Heading>
  );
}

/** Eyebrow + title + description block used by most sections. */
export function SectionHeader({
  ctx,
  node,
  eyebrow,
  title,
  description,
  align = "left",
  className,
  children,
}: {
  ctx: RenderContext;
  node: SectionNode;
  eyebrow?: string;
  title?: string;
  description?: string;
  align?: "left" | "center";
  className?: string;
  children?: ReactNode;
}) {
  if (!title && !eyebrow && !description) return null;
  const aligned = node.style?.align ? (node.style.align === "center" ? "center" : "left") : align;
  return (
    <div className={cx(REVEAL, "max-w-2xl", aligned === "center" && "mx-auto text-center", className)}>
      <Eyebrow ctx={ctx} text={eyebrow} path="content.eyebrow" className="mb-3" />
      {title ? <SectionTitle ctx={ctx} node={node} text={title} /> : null}
      {description ? (
        <MultiLine ctx={ctx} text={description} path="content.description" className="mt-4 text-base leading-relaxed text-site-muted @3xl:text-lg" />
      ) : null}
      {children}
    </div>
  );
}

/** Multi-line text as React text only: blank line → paragraph, newline → <br/>. */
export function MultiLine({ ctx, text, path, className, paragraphClassName }: { ctx: RenderContext; text?: string; path?: string; className?: string; paragraphClassName?: string }) {
  const paras = paragraphs(text);
  if (paras.length === 0) return null;
  const attrs = path ? field(ctx, path) : {};
  const renderLines = (lines: string[]) =>
    lines.map((line, i) => (
      <span key={i}>
        {i > 0 ? <br /> : null}
        {line}
      </span>
    ));
  if (paras.length === 1) {
    return (
      <p className={className} {...attrs}>
        {renderLines(paras[0]!)}
      </p>
    );
  }
  return (
    <div className={cx("space-y-4", className)} {...attrs}>
      {paras.map((lines, i) => (
        <p key={i} className={paragraphClassName}>
          {renderLines(lines)}
        </p>
      ))}
    </div>
  );
}

// ── Links & buttons ────────────────────────────────────────────────────────────

export interface LinkProps {
  ctx: RenderContext;
  href: string | null | undefined;
  children: ReactNode;
  className?: string;
  whatsapp?: boolean;
  /** Count clicks as `link_click` in site analytics (outbound links, affiliate picks). */
  trackLink?: boolean;
  /** Paid/affiliate link: rel="sponsored nofollow" (Google's guidance for affiliate links). */
  sponsored?: boolean;
  ariaLabel?: string;
  /** Rendered when the href is unsafe/missing. Defaults to a plain <span>. */
  fallback?: "span" | "none";
  fieldPath?: string;
}

/**
 * The only way the engine renders <a>. Re-checks the href, marks WhatsApp, phone, email and outbound
 * links for the click beacon, opens external links safely, and makes links inert (data-baze-inert) in the editor.
 */
export function SiteLink({ ctx, href, children, className, whatsapp, trackLink, sponsored, ariaLabel, fallback = "span", fieldPath }: LinkProps) {
  // A bare "#" (top of the page, the one-page home link) is engine-made; owner hrefs never are.
  const safe = href === "#" ? href : safeHref(href);
  const f = fieldPath ? field(ctx, fieldPath) : {};
  if (!safe) {
    return fallback === "none" ? null : (
      <span className={className} {...f}>
        {children}
      </span>
    );
  }
  const external = isExternalHref(safe);
  const rel = [sponsored && "sponsored nofollow", external && "noopener noreferrer"].filter(Boolean).join(" ");
  // Contact clicks are counted (and sent to Meta as Contact when the site has a Pixel).
  const track = whatsapp ? "whatsapp" : safe.startsWith("tel:") ? "phone" : safe.startsWith("mailto:") ? "email" : trackLink ? "link" : null;
  return (
    <a
      href={safe}
      className={className}
      aria-label={ariaLabel}
      {...(external ? { target: "_blank" } : {})}
      {...(rel ? { rel } : {})}
      {...(track ? { "data-baze-track": track } : {})}
      {...(ctx.editor ? { "data-baze-inert": "" } : {})}
      {...f}
    >
      {children}
    </a>
  );
}

export interface CtaLike {
  label: string;
  kind: "whatsapp" | "link";
  href?: string;
  message?: string;
}

export interface ResolvedCta {
  href: string | null;
  whatsapp: boolean;
  icon: IconName | null;
}

/** WhatsApp CTAs use the site's number (+ message template); links are re-checked. */
export function resolveCta(ctx: RenderContext, cta: CtaLike, produk?: string): ResolvedCta {
  if (cta.kind === "whatsapp") {
    const wa = waLink(ctx.site.contact.whatsapp, cta.message ?? ctx.site.whatsapp?.defaultMessage, messageVars(ctx, produk));
    if (wa) return { href: wa, whatsapp: true, icon: "whatsapp" };
    const tel = telHref(ctx.site.contact.phone);
    return { href: tel, whatsapp: false, icon: tel ? "phone" : null };
  }
  const href = safeHref(cta.href);
  return { href, whatsapp: false, icon: href && isExternalHref(href) ? "arrow-up-right" : null };
}

/** Whether a CTA renders anything: always in the editor (as a placeholder), live only with a working link. */
export function ctaVisible(ctx: RenderContext, cta: CtaLike | undefined): boolean {
  return !!cta && (ctx.editor || !!resolveCta(ctx, cta).href);
}

const BUTTON_BASE =
  "group/btn inline-flex min-h-12 items-center justify-center gap-2 rounded-site-button px-6 py-3 text-[0.9375rem] font-semibold leading-none whitespace-nowrap transition duration-200 motion-reduce:transition-none";

export const BUTTON = {
  primary: `${BUTTON_BASE} bg-site-primary text-site-on-primary shadow-site-sm hover:-translate-y-px hover:shadow-site-md active:translate-y-0`,
  secondary: `${BUTTON_BASE} border border-site-border bg-transparent text-site-text hover:border-site-text/40 hover:bg-site-text/5`,
  inverse: `${BUTTON_BASE} bg-site-on-primary text-site-primary shadow-site-sm hover:-translate-y-px hover:shadow-site-md`,
  glass: `${BUTTON_BASE} border border-white/40 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20`,
} as const;

export type ButtonStyle = keyof typeof BUTTON;

/** A CTA rendered as a button-styled link. Unusable CTAs are omitted live and flagged in the editor. */
export function CtaButton({
  ctx,
  cta,
  path,
  style = "primary",
  className,
  produk,
  withArrow,
}: {
  ctx: RenderContext;
  cta: CtaLike | undefined;
  path: string;
  style?: ButtonStyle;
  className?: string;
  produk?: string;
  withArrow?: boolean;
}) {
  if (!cta) return null;
  const r = resolveCta(ctx, cta, produk);
  if (!r.href) {
    if (!ctx.editor) return null;
    return (
      <span className={cx(BUTTON[style], "cursor-not-allowed opacity-60 outline-2 outline-dashed outline-current/40", className)} title="Tautan belum diatur" {...field(ctx, path)}>
        {cta.label}
      </span>
    );
  }
  return (
    <SiteLink ctx={ctx} href={r.href} whatsapp={r.whatsapp} className={cx("baze-btn", BUTTON[style], className)}>
      {r.icon ? <Icon name={r.icon} className="size-[1.125rem] shrink-0" /> : null}
      <span {...field(ctx, `${path}.label`)}>{cta.label}</span>
      {withArrow && !r.icon ? <Icon name="arrow-right" className="size-4 shrink-0 transition-transform duration-200 group-hover/btn:translate-x-0.5 motion-reduce:transition-none" /> : null}
    </SiteLink>
  );
}

/** Adds ?untuk=<item> to a same-site path, so the page it opens (e.g. a donation form) knows which card sent the visitor. */
function withItem(href: string, produk: string): string {
  if (!href.startsWith("/") || href.startsWith("//")) return href;
  const hash = href.indexOf("#");
  const [base, frag] = hash < 0 ? [href, ""] : [href.slice(0, hash), href.slice(hash)];
  return `${base}${base.includes("?") ? "&" : "?"}untuk=${encodeURIComponent(produk)}${frag}`;
}

/**
 * Small "Pesan" order link for menu/product items (WhatsApp, with {{produk}}). An item `cta` overrides it:
 * its label and message, or (kind "link") a link to another page instead of WhatsApp.
 */
export function OrderLink({ ctx, message, produk, className, label = "Pesan", cta }: { ctx: RenderContext; message?: string; produk: string; className?: string; label?: string; cta?: CtaLike }) {
  if (cta?.kind === "link") {
    const href = safeHref(cta.href);
    if (!href) return null;
    return (
      <SiteLink ctx={ctx} href={withItem(href, produk)} trackLink className={className} ariaLabel={`${cta.label} ${produk}`}>
        <span>{cta.label}</span>
        <Icon name={isExternalHref(href) ? "arrow-up-right" : "arrow-right"} className="size-4 shrink-0" />
      </SiteLink>
    );
  }
  const text = cta?.label ?? label;
  const href = waLink(ctx.site.contact.whatsapp, cta?.message ?? message ?? ctx.site.whatsapp?.defaultMessage, messageVars(ctx, produk));
  if (!href) return null;
  return (
    <SiteLink ctx={ctx} href={href} whatsapp className={className} ariaLabel={`${text} ${produk} via WhatsApp`}>
      <Icon name="whatsapp" className="size-4 shrink-0" />
      <span>{text}</span>
    </SiteLink>
  );
}

// ── Media ──────────────────────────────────────────────────────────────────────

export function isPriority(ctx: RenderContext, node: { id: string }): boolean {
  return !ctx.thumbnail && ctx.priorityImageSectionId === node.id;
}

export function Img({
  ctx,
  image,
  sizes,
  className,
  priority = false,
  path,
  target,
}: {
  ctx: RenderContext;
  image: ImageLike | undefined;
  sizes: string;
  className?: string;
  priority?: boolean;
  path?: string;
  /** Preferred width for the fallback src. */
  target?: number;
}) {
  if (!image) return null;
  // Thumbnails are a 1280px layout scaled to a few hundred px: never fetch the full-size variants.
  const src = imageSrc(image, ctx.thumbnail ? Math.min(target ?? 960, 960) : target);
  if (!src) return null;
  const set = srcSet(image);
  if (ctx.thumbnail) sizes = "480px";
  const pos = objectPosition(image);
  return (
    <img
      src={src}
      srcSet={set}
      sizes={set ? sizes : undefined}
      alt={image.alt ?? ""}
      width={image.width}
      height={image.height}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "auto" : "async"}
      fetchPriority={priority ? "high" : undefined}
      style={pos ? { objectPosition: pos } : undefined}
      className={cx("baze-img h-full w-full object-cover", className)}
      {...(path ? field(ctx, path) : {})}
    />
  );
}

/** Placeholder shown in the editor where an optional image is missing. */
export function EditorImagePlaceholder({ ctx, path, className }: { ctx: RenderContext; path: string; className?: string }) {
  if (!ctx.editor) return null;
  return (
    <div className={cx("grid h-full w-full place-items-center bg-site-surface text-sm text-site-muted", className)} {...field(ctx, path)}>
      Tambahkan foto
    </div>
  );
}

// ── Commerce bits ──────────────────────────────────────────────────────────────

/** "Rp 25.000" with an optional note: "mulai dari" before, "/jam" style after. */
export function Price({ value, note, className, noteClassName }: { value?: number; note?: string; className?: string; noteClassName?: string }) {
  if (value === undefined && !note) return null;
  const suffix = !!note && /^(?:\/|per\s)/i.test(note);
  return (
    <span className={cx("inline-flex flex-wrap items-baseline gap-x-1.5", className)}>
      {note && !suffix ? <span className={cx("text-xs font-medium text-site-muted", noteClassName)}>{note}</span> : null}
      {value !== undefined ? <span className="font-semibold tabular-nums">{formatRupiah(value)}</span> : null}
      {note && suffix ? <span className={cx("text-xs font-medium text-site-muted", noteClassName)}>{note}</span> : null}
    </span>
  );
}

export function Stars({ rating, className }: { rating: number; className?: string }) {
  const r = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <span role="img" aria-label={`Rating ${r} dari 5`} className={cx("inline-flex gap-0.5 text-site-accent", className)}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Icon key={i} name="star" className={cx("size-4", i >= r && "opacity-25")} />
      ))}
    </span>
  );
}

export function Chip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cx("inline-flex items-center gap-1.5 rounded-full border border-site-border bg-site-surface px-3 py-1 text-xs font-medium text-site-text", className)}>
      {children}
    </span>
  );
}

/** Editor-only hint for sections whose data comes from Info bisnis and is still empty. */
export function EditorHint({ ctx, children }: { ctx: RenderContext; children: ReactNode }) {
  if (!ctx.editor) return null;
  return <p className="rounded-site-md border border-dashed border-site-border px-4 py-3 text-sm text-site-muted">{children}</p>;
}

// ── Decoration ─────────────────────────────────────────────────────────────────

/** Hairline flourish with a centred leaf motif (quotes, invitations). Decorative only. */
export function Ornament({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 240 24" aria-hidden="true" focusable="false" className={cx("h-6 w-60 max-w-full text-site-primary", className)} fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
      <path d="M8 12h78" opacity="0.55" />
      <path d="M154 12h78" opacity="0.55" />
      <path d="M120 3c4 5 4 13 0 18-4-5-4-13 0-18Z" fill="currentColor" fillOpacity="0.15" />
      <path d="M120 12c6-6 14-7 22-3-8 4-16 3-22 3Zm0 0c-6-6-14-7-22-3 8 4 16 3 22 3Z" fill="currentColor" fillOpacity="0.1" />
      <circle cx="92" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="148" cy="12" r="1.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Ticker band. `children` is the real (readable) run; `repeat` must render at the same width, because the
 * track loops by translating half of itself. Repeats are aria-hidden and only shown while motion runs, so
 * the editor, thumbnails and reduced-motion visitors see `children` alone and static.
 */
export function Marquee({ children, repeat, seconds = 28, className }: { children: ReactNode; repeat: ReactNode; seconds?: number; className?: string }) {
  return (
    <div className={cx("baze-marquee overflow-hidden", className)} style={{ ["--baze-marquee-t" as string]: `${seconds}s` } as CSSProperties}>
      <div className="baze-marquee-track">
        <div className="baze-marquee-run">{children}</div>
        {[0, 1, 2].map((i) => (
          <div key={i} aria-hidden="true" className="baze-marquee-run baze-marquee-copy">
            {repeat}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Horizontal scroll-snap rail (carousels). Keyboard users can focus and scroll it; no JavaScript.
 * Children should be <li> elements with a fixed basis and `snap-start`.
 */
export function Rail({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className="relative">
      <ul
        role="list"
        tabIndex={0}
        aria-label={label}
        className={cx(
          "baze-rail -mx-5 flex snap-x snap-mandatory scroll-px-5 gap-4 overflow-x-auto px-5 pb-4 @3xl:-mx-8 @3xl:scroll-px-8 @3xl:gap-6 @3xl:px-8",
          className,
        )}
      >
        {children}
      </ul>
      <p aria-hidden="true" className="mt-2 flex items-center justify-center gap-2 text-xs font-medium tracking-wide text-site-muted @3xl:hidden">
        <Icon name="chevron-left" className="size-3.5" />
        Geser untuk melihat
        <Icon name="chevron-right" className="size-3.5" />
      </p>
    </div>
  );
}

const SLIDE_CONTROL = {
  light: "bg-black/35 text-white ring-1 ring-white/25 backdrop-blur-sm hover:bg-black/55",
  site: "bg-site-bg text-site-text ring-1 ring-site-border shadow-site-sm hover:border-site-primary hover:text-site-primary",
} as const;
const SLIDE_DOT = {
  light: "bg-white/45 aria-[current=true]:bg-white",
  site: "bg-site-border aria-[current=true]:bg-site-primary",
} as const;

/**
 * One-at-a-time image slider. The engine renders plain HTML: a full-width scroll-snap track that can be
 * swiped or keyboard-scrolled with no JavaScript. On live sites the web runtime's slider script
 * (apps/web lib/slider.ts) reveals the controls, wires them to the track and autoplays. The controls
 * stay `hidden` on live renders until that script runs. The editor and thumbnails show them
 * (inert) so the layout is truthful there.
 */
export function Slideshow({
  ctx,
  label,
  slides,
  thumbs,
  tone = "site",
  interval = 6000,
  pauseOnHover = true,
  className,
  trackClassName,
  controlsClassName,
}: {
  ctx: RenderContext;
  label: string;
  slides: ReactNode[];
  /** Thumbnail per slide (gallery). Rendered as buttons that jump to that slide. */
  thumbs?: ReactNode[];
  tone?: keyof typeof SLIDE_CONTROL;
  /** Autoplay delay in ms (0 = no autoplay). */
  interval?: number;
  pauseOnHover?: boolean;
  className?: string;
  trackClassName?: string;
  controlsClassName?: string;
}) {
  const n = slides.length;
  const hideUi = !(ctx.editor || ctx.thumbnail);
  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
      data-baze-slider=""
      data-interval={interval}
      data-hover={pauseOnHover ? undefined : "0"}
      // Positioned by the caller (e.g. absolute behind the hero copy); relative otherwise, for the controls.
      className={className ?? "relative"}
    >
      <ul role="list" tabIndex={0} aria-label={label} data-baze-slide-track="" className={cx("baze-slider flex snap-x snap-mandatory overflow-x-auto", trackClassName)}>
        {slides.map((slide, i) => (
          <li key={i} aria-roledescription="slide" aria-label={`${i + 1} dari ${n}`} className="relative h-full w-full shrink-0 snap-center snap-always">
            {slide}
          </li>
        ))}
      </ul>
      {n > 1 ? (
        <div data-baze-slide-ui="" hidden={hideUi} className={cx("flex items-center gap-3", controlsClassName)}>
          <button type="button" data-baze-slide-prev="" aria-label="Foto sebelumnya" className={cx("grid size-10 place-items-center rounded-full transition", SLIDE_CONTROL[tone])}>
            <Icon name="chevron-left" className="size-4" />
          </button>
          <div className="flex items-center gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                data-baze-slide-to={i}
                aria-label={`Foto ${i + 1}`}
                aria-current={i === 0 ? "true" : undefined}
                className={cx("baze-slide-dot h-2 w-2 rounded-full transition-all aria-[current=true]:w-6", SLIDE_DOT[tone])}
              />
            ))}
          </div>
          <button type="button" data-baze-slide-next="" aria-label="Foto berikutnya" className={cx("grid size-10 place-items-center rounded-full transition", SLIDE_CONTROL[tone])}>
            <Icon name="chevron-right" className="size-4" />
          </button>
        </div>
      ) : null}
      {thumbs && n > 1 ? (
        <ul data-baze-slide-ui="" hidden={hideUi} role="list" className="baze-rail mt-3 flex gap-2 overflow-x-auto pb-1">
          {thumbs.map((thumb, i) => (
            <li key={i} className="shrink-0">
              <button
                type="button"
                data-baze-slide-to={i}
                aria-label={`Lihat foto ${i + 1}`}
                aria-current={i === 0 ? "true" : undefined}
                className="block h-16 w-24 overflow-hidden rounded-site-sm opacity-60 ring-2 ring-transparent transition hover:opacity-100 aria-[current=true]:opacity-100 aria-[current=true]:ring-site-primary @3xl:h-20 @3xl:w-28"
              >
                {thumb}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
